import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';

import { Box, Button, Stack, Typography } from '@mui/material';

import { HERO_IMAGE_COUNT } from '../constants';
import { ImageScore } from '../types';

import SortableCurationImageCard from './SortableCurationImageCard';

interface Props {
  images: Array<ImageScore>;
  onSave: (orderedImageIds: Array<string>) => void;
  isSaving: boolean;
  onImageClick: (image: ImageScore) => void;
  onOrderChange?: (orderedImageIds: Array<string>) => void;
}

export default function SortableImageGrid({ images, onSave, isSaving, onImageClick, onOrderChange }: Props) {
  const [orderedImages, setOrderedImages] = useState<Array<ImageScore>>(images);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setOrderedImages(images);
    setHasChanges(false);
  }, [images]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const itemIds = useMemo(() => orderedImages.map((img) => img.id), [orderedImages]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = orderedImages.findIndex((img) => img.id === active.id);
      const newIndex = orderedImages.findIndex((img) => img.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const newOrder = arrayMove(orderedImages, oldIndex, newIndex);
      setOrderedImages(newOrder);
      setHasChanges(true);
      onOrderChange?.(newOrder.map((img) => img.id));
    },
    [orderedImages, onOrderChange],
  );

  const handleReset = useCallback(() => {
    setOrderedImages(images);
    setHasChanges(false);
  }, [images]);

  const handleSave = useCallback(() => {
    onSave(orderedImages.map((img) => img.id));
  }, [orderedImages, onSave]);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="subtitle2">
          Images ({orderedImages.length}) — first {HERO_IMAGE_COUNT} are heroes
        </Typography>
        {hasChanges && (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="text" onClick={handleReset} disabled={isSaving}>
              Reset
            </Button>
            <Button size="small" variant="contained" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Order'}
            </Button>
          </Stack>
        )}
      </Stack>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
        <SortableContext items={itemIds} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
              maxHeight: 800,
              overflowY: 'auto',
              p: 0.5,
            }}
          >
            {orderedImages.map((img, index) => (
              <Box
                key={img.id}
                sx={{
                  ...(index < HERO_IMAGE_COUNT && {
                    outline: (theme) => `2px solid ${theme.palette.secondary.main}`,
                    borderRadius: 1,
                  }),
                }}
              >
                <SortableCurationImageCard image={img} isHero={index < HERO_IMAGE_COUNT} onClick={onImageClick} />
              </Box>
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
